
/**
 * Version Alert Component - Displays a notification about the new portal design version.
 * Features:
 * - Allows users to try the new design version
 * - Provides option to temporarily hide the alert
 * - Allows users to permanently disable the alert via profile settings
 */

import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { IUserUpdate, User } from '@/models/main';
import { fetchUpdateProfile } from '@/helpers/fetchers';

@Component({
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class UIVersionAlert extends Vue {
  public isVisible = false;
  public isHiding = false;
  private user!: User;

  get newVersionUrl(): string | undefined {
    return process.env.VUE_APP_NEW_VERSION_URL;
  }

  /**
   * Watches for user data changes and updates alert visibility
   * @param newUser Updated user data from store
   */
  @Watch('user', { immediate: true })
  private onUserChange(newUser: User): void {
    if (newUser?.show_new_ui_notice !== undefined) {
      this.isVisible = newUser.show_new_ui_notice;
    }
  }

  /**
   * Temporarily hides the alert without updating user preferences
   */
  public hideAlert(): void {
    this.isVisible = false;
  }

  /**
   * Permanently disables the alert by updating user preferences
   * If the update fails, the alert remains visible
   */
  public async hideAlertPermanently(): Promise<void> {
    try {
      this.isHiding = true;
      const formData = new FormData();
      const payload: IUserUpdate = {
        show_new_ui_notice: false,
      };
      formData.append('payload', JSON.stringify(payload));

      const response = await fetchUpdateProfile(formData);
      if (response) {
        this.$store.dispatch('moduleUser/setUser', response.data);
        this.hideAlert();
      }
    } catch (error) {
      // Keep the alert visible on error
      this.isVisible = true;
    } finally {
      this.isHiding = false;
    }
  }
}
